import { css } from 'styled-components';
import { math } from 'polished';
import { SDS_SPACE_XXXSMALL, SDS_SPACE_XXSMALL, SDS_SPACE_SMALL, SDS_SPACE_MEDIUM, SDS_SPACE_XXLARGE, SDS_SPACE_XXXLARGE, SDS_SPACE_XXXXLARGE, SDS_SPACE_XSMALL, SDS_SPACE_LARGE, SDS_SPACE_XLARGE } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { SDS_SIZE_BORDER_RADIUS_XXXSMALL, SDS_SIZE_BORDER_RADIUS_NONE, SDS_SIZE_BORDER_SMALL, SDS_SIZE_BORDER_XLARGE } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_FONT_WEIGHT_MEDIUM, SDS_FONT_FAMILY_PRIMARY, SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL, SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM, SDS_FONT_WEIGHT_REGULAR, SDS_FONT_SIZE_HEADING_XSMALL, SDS_FONT_LINEHEIGHT_HEADING_XSMALL, SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { bordered, scaledIcon } from '../common/util.js';
import { boxSide } from '../common/constants.js';

const backgroundColor = ({ theme, status, variant, }) => variant === 'tonal'
    ? status
        ? {
            info: theme.colors.SDS_BRAND_COLOR_BACKGROUND_INFO,
            success: theme.colors.SDS_BRAND_COLOR_BACKGROUND_SUCCESS,
            warning: theme.colors.SDS_BRAND_COLOR_BACKGROUND_WARNING,
            error: theme.colors.SDS_BRAND_COLOR_BACKGROUND_ERROR,
        }[status]
        : theme.colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_GREY
    : variant === 'outlined'
        ? theme.colors.SDS_BRAND_COLOR_ELEVATION_FLAT
        : undefined;
const borderColor = ({ theme, status, variant, }) => variant === 'outlined'
    ? status
        ? {
            info: theme.colors.SDS_BRAND_COLOR_BORDER_INFO,
            success: theme.colors.SDS_BRAND_COLOR_BORDER_SUCCESS,
            warning: theme.colors.SDS_BRAND_COLOR_BORDER_WARNING,
            error: theme.colors.SDS_BRAND_COLOR_BORDER_ERROR,
        }[status]
        : theme.colors.SDS_BRAND_COLOR_BORDER_DEFAULT_GREY
    : 'transparent';
const headerColor = ({ theme, status }) => status
    ? {
        info: theme.colors.SDS_BRAND_COLOR_TEXT_INFO,
        success: theme.colors.SDS_BRAND_COLOR_TEXT_SUCCESS,
        warning: theme.colors.SDS_BRAND_COLOR_TEXT_WARNING,
        error: theme.colors.SDS_BRAND_COLOR_TEXT_ERROR,
    }[status]
    : theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY;
const iconColor = ({ theme, status }) => status
    ? {
        info: theme.colors.SDS_BRAND_COLOR_ELEMENT_INFO,
        success: theme.colors.SDS_BRAND_COLOR_ELEMENT_SUCCESS,
        warning: theme.colors.SDS_BRAND_COLOR_ELEMENT_WARNING,
        error: theme.colors.SDS_BRAND_COLOR_ELEMENT_ERROR,
    }[status]
    : theme.colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_GREY;
const borderRadius = ({ rounding }) => rounding === 'rounded'
    ? SDS_SIZE_BORDER_RADIUS_XXXSMALL
    : SDS_SIZE_BORDER_RADIUS_NONE;
const borderWidths = [
    SDS_SIZE_BORDER_SMALL,
    SDS_SIZE_BORDER_SMALL,
    SDS_SIZE_BORDER_SMALL,
    SDS_SIZE_BORDER_XLARGE, // left
];
const borderWidth = ({ variant }) => variant === 'plain' ? 0 : borderWidths.join(' ');
const contentFlex = ({ alignment }) => alignment === 'center' ? 0 : 1;
const contentDirection = ({ orientation }) => orientation === 'horizontal' ? 'row' : 'column';
const contentTextAlign = ({ alignment }) => alignment === 'center' ? 'center' : 'left';
const headerFontMetrics = {
    // sizing: [font-size, line-height]
    small: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    medium: [SDS_FONT_SIZE_HEADING_XSMALL, SDS_FONT_LINEHEIGHT_HEADING_XSMALL],
    large: [SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL],
};
const headerFont = ({ sizing }) => sizing &&
    [headerFontMetrics[sizing]]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_MEDIUM} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const headerMargin = ({ sizing }) => sizing &&
    {
        // sizing: margin-bottom
        small: 0,
        medium: SDS_SPACE_XXXSMALL,
        large: SDS_SPACE_XXSMALL,
    }[sizing];
const messageFont = ({ sizing }) => sizing &&
    [
        {
            // sizing: [font-size, line-height]
            small: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL],
            medium: [
                SDS_FONT_SIZE_BODY_MEDIUM,
                SDS_FONT_LINEHEIGHT_BODY_MEDIUM,
            ],
            large: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
        }[sizing],
    ]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_REGULAR} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
function innerSpacing({ sizing }) {
    return (sizing &&
        {
            // sizing: gap
            small: SDS_SPACE_XXSMALL,
            medium: SDS_SPACE_SMALL,
            large: SDS_SPACE_MEDIUM,
        }[sizing]);
}
const outerPaddingWidths = {
    // sizing: [vertical, horizontal]
    small: [SDS_SPACE_XSMALL, SDS_SPACE_SMALL],
    medium: [SDS_SPACE_MEDIUM, SDS_SPACE_LARGE],
    large: [SDS_SPACE_LARGE, SDS_SPACE_XLARGE],
};
const outerPadding = ({ sizing, variant }) => variant === 'plain'
    ? 0
    : sizing &&
        [outerPaddingWidths[sizing]].map(paddings => [...paddings, ...paddings] // [top, right, bottom, left]
            .map((sidePadding, index) => bordered(sidePadding, borderWidths[index]))
            .join(' '));
// A wider left padding used only with center alignment when close button is
// enabled to balance the centered content optically to the horizontal
// center of the component.
const centeredLeftPadding = ({ alignment, close, sizing }) => alignment === 'center' && close && sizing
    ? [
        {
            // sizing: padding-left
            small: SDS_SPACE_XXLARGE,
            medium: SDS_SPACE_XXXLARGE,
            large: SDS_SPACE_XXXXLARGE,
        }[sizing],
    ]
        .map(sidePadding => bordered(sidePadding, borderWidths[boxSide.LEFT]))
        .join('')
    : undefined;
const iconAlignment = ({ alignment, orientation }) => alignment === 'center' || orientation === 'horizontal'
    ? 'center'
    : 'flex-start';
const iconSize = ({ sizing }) => sizing && scaledIcon(headerFontMetrics[sizing]).iconSize;
const iconMargin = ({ sizing }) => sizing && scaledIcon(headerFontMetrics[sizing]).iconMargin;
const closeAlignment = ({ alignment }) => alignment === 'center' ? 'center' : 'flex-start';
// TODO: Depends on sizing, check medium / large
const centeredMargin = ({ alignment }) => alignment === 'center' ? 'auto' : 0;
// Negative margins are used in close button container to override the effect
// of outer padding and inner spacing. The actual padding around the close
// button container is defined in `closePadding` below.
const closeMargin = ({ sizing }) => sizing &&
    [outerPaddingWidths[sizing]].map(margins => [
        ...margins,
        margins[0],
        innerSpacing({ sizing }), // left: inner spacing
    ]
        .map(margin => math(`-1 * ${margin}`))
        .join(' '));
const closePadding = ({ sizing }) => sizing &&
    {
        // sizing: padding
        small: SDS_SPACE_XXXSMALL,
        medium: SDS_SPACE_XSMALL,
        large: SDS_SPACE_MEDIUM,
    }[sizing];
const NotificationStyles = css `
    background-color: ${backgroundColor};
    border-color: ${borderColor};
    border-radius: ${borderRadius};
    border-style: solid;
    border-width: ${borderWidth};
    color: ${({ theme }) => theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY};
    display: flex;
    flex-direction: row;
    font: ${messageFont};
    gap: ${innerSpacing};
    justify-content: center;
    padding: ${outerPadding};
    padding-left: ${centeredLeftPadding};

    .header {
        color: ${headerColor};
        font: ${headerFont};
        margin-bottom: ${headerMargin};

        > h2,
        > h3,
        > h4,
        > h5,
        > h6 {
            color: inherit;
            font: inherit;
            margin: inherit;
        }
    }

    .trailer {
        margin: auto 0;
    }

    /* This is either icon container or content container depending on props */
    > :first-child {
        margin-left: ${centeredMargin};
    }

    .icon-container {
        align-items: ${iconAlignment};
        color: ${iconColor};
        display: flex;
    }

    .leading-icon {
        height: ${iconSize};
        margin: ${iconMargin};
        width: ${iconSize};

        svg {
            height: auto;
            width: auto;
        }
    }

    .content-container {
        display: flex;
        flex: ${contentFlex};
        flex-basis: ${({ contentInitialWidth }) => contentInitialWidth};
        flex-direction: ${contentDirection};
        gap: ${innerSpacing};
        justify-content: space-between;
        margin-right: ${centeredMargin};
        text-align: ${contentTextAlign};
    }

    .close-container {
        align-items: ${closeAlignment};
        display: flex;
        margin: ${closeMargin};
        padding: ${closePadding};
    }
`;

export { NotificationStyles };
//# sourceMappingURL=Notification.styles.js.map
